import React from "react";
import { cn } from "@/lib/utils";
import { getEngineTypeIcon, EngineWizardType } from "@/utils/engineTypeIcons";

interface EngineIconProps {
  engineType: EngineWizardType;
  size?: "sm" | "md" | "lg" | "xl" | "hero";
  className?: string;
  context?: 'landing' | 'navigation';
  /** Density mode: 'card' renders the inner artwork larger (75% vs 55%) */
  density?: 'default' | 'card';
  /** When true, renders only the engine turbine without action symbol (legacy, now ignored) */
  simple?: boolean;
}

// Size classes - these define the OUTER container
const sizeClasses = {
  sm: "w-20 h-12",
  md: "w-28 h-18",
  lg: "w-36 h-24",
  xl: "w-52 h-32",
  hero: "w-72 h-44 md:w-88 md:h-52 lg:w-104 lg:h-60",
};

export const EngineIcon: React.FC<EngineIconProps> = ({
  engineType,
  size = "md",
  className,
  context = 'landing',
  density = 'default',
  simple = false,
}) => {
  // Get the category-specific icon
  const iconSrc = getEngineTypeIcon(engineType);
  
  // Filter classes for navigation context (dark icons on white background, inverted in dark mode)
  const filterClass = context === 'navigation'
    ? "brightness-0 dark:invert"
    : "brightness-0 invert";

  // Simple mode: bypass sizeClasses, let className control sizing, fill container directly
  if (simple) {
    return (
      <div
        className={cn(
          "flex items-center justify-center flex-shrink-0",
          className
        )}
      >
        <img 
          src={iconSrc} 
          alt="Engine" 
          className={cn("w-[85%] h-[85%] object-contain", filterClass)}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center justify-center flex-shrink-0",
        sizeClasses[size],
        className
      )}
    >
      {/* Inner wrapper - scales content to match helicopter icon visual density */}
      {/* density='card' uses 90% for mobile wizard cards, default uses 55% */}
      <div className={cn(
        "flex items-center justify-center",
        density === 'card' ? "w-[90%] h-[90%]" : "w-[55%] h-[55%]"
      )}>
        <img 
          src={iconSrc} 
          alt="Engine" 
          className={cn("h-full w-auto object-contain", filterClass)}
        />
      </div>
    </div>
  );
};
